import type { ChatView } from '#contract'
import { text } from '#contract'
import { useView } from '../../../app/use-view.ts'
import { weekdayName } from '../../../i18n/dates.ts'
import { useLocale, useT } from '../../../i18n/index.ts'
import { formatOsTime, gameDay, gameNow, timeOfDay } from '../../clock.ts'
import { Avatar } from './avatar.tsx'
import styles from './wassup.module.css'

interface ChatListProps {
  readonly chats: readonly ChatView[]
  readonly selected: string | null
  readonly onOpen: (id: string) => void
}

/** Every chat, the latest first: avatar, name, the last message on one line, when, unread. */
export function ChatList({ chats, selected, onOpen }: ChatListProps) {
  const t = useT()
  return (
    <ul class={styles.list} aria-label={t(text('os.app.wassup.chats'))}>
      {chats.map((chat) => {
        const last = chat.messages.at(-1)
        return (
          <li key={chat.id}>
            <button
              type="button"
              class={styles.row}
              data-selected={chat.id === selected || undefined}
              onClick={() => {
                onOpen(chat.id)
              }}
            >
              <Avatar chat={chat} />
              <span class={styles.rowText}>
                <span class={styles.rowTop}>
                  <span class={styles.name}>{t(chat.name)}</span>
                  {last && (
                    <span class={styles.when} data-unread={chat.unread > 0 || undefined}>
                      <When at={last.at} />
                    </span>
                  )}
                </span>
                <span class={styles.rowBottom}>
                  <span class={styles.preview}>{last ? t(last.text) : ''}</span>
                  {chat.unread > 0 && (
                    <span
                      class={styles.unread}
                      aria-label={t(text('os.app.wassup.unread', { n: chat.unread }))}
                    >
                      {chat.unread}
                    </span>
                  )}
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/** The time today, the weekday within the last week, else the week ("Week 2"). */
function When({ at }: { readonly at: number }) {
  const t = useT()
  const locale = useLocale()
  const now = useView((view) => gameNow(view.os.day, view.os.time))
  const day = gameDay(at)
  const today = now === null ? day : gameDay(now)
  if (day === today) return <>{formatOsTime(timeOfDay(at))}</>
  if (today - day < 7) return <>{weekdayName(locale, (day - 1) % 7)}</>
  return <>{t(text('calendar.week', { n: Math.floor((day - 1) / 7) + 1 }))}</>
}
